/**
 * ProductReviews - Reviews display and submission for store products
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Star,
  ThumbsUp,
  CheckCircle,
  MessageSquare,
  ChevronDown,
  Send,
  Loader2,
  User,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star rating component
const StarRating = ({ rating, onRate, interactive = false, size = 'md' }) => {
  const [hovered, setHovered] = useState(0);
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          onClick={() => interactive && onRate?.(star)}
          className={`${interactive ? 'cursor-pointer' : 'cursor-default'} transition-colors`}
        >
          <Star
            className={`${sizes[size]} ${
              star <= (hovered || rating)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-zinc-700 text-zinc-700'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Rating distribution bar
const RatingBar = ({ rating, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-3 text-zinc-400">{rating}</span>
      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
      <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-400 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="w-8 text-right text-zinc-500">{count}</span>
    </div>
  );
};

// Single review card
const ReviewCard = ({ review, onMarkHelpful, userId }) => {
  const [marking, setMarking] = useState(false);

  const handleMarkHelpful = async () => {
    if (!userId) {
      toast.error('Please login to mark reviews as helpful');
      return;
    }
    
    setMarking(true);
    try {
      await onMarkHelpful(review.id);
    } finally {
      setMarking(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-800"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-500 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-white">{review.user_name}</span>
              {review.verified_purchase && (
                <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                  <CheckCircle className="w-3 h-3" />
                  Verified Purchase
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <StarRating rating={review.rating} size="sm" />
              <span className="text-xs text-zinc-500">
                {new Date(review.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {review.title && (
        <h4 className="font-semibold text-white mb-2">{review.title}</h4>
      )}
      
      <p className="text-zinc-300 text-sm leading-relaxed mb-3">
        {review.review_text}
      </p>

      <button
        onClick={handleMarkHelpful}
        disabled={marking || review.user_id === userId}
        className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-50"
      >
        {marking ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ThumbsUp className="w-4 h-4" />
        )}
        Helpful ({review.helpful_count || 0})
      </button>
    </motion.div>
  );
};

// Review submission form
const ReviewForm = ({ productId, onSubmit, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (!text.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ rating, title, review_text: text });
      setRating(0);
      setTitle('');
      setText('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-800">
      <h4 className="font-semibold text-white mb-4">Write a Review</h4>
      
      <div className="mb-4">
        <label className="block text-sm text-zinc-400 mb-2">Your Rating</label>
        <StarRating rating={rating} onRate={setRating} interactive size="lg" />
      </div>

      <div className="mb-4">
        <label className="block text-sm text-zinc-400 mb-2">Review Title (optional)</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Sum up your thoughts"
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm text-zinc-400 mb-2">Your Review</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What did you like or dislike? How did you use this product?"
          rows={4}
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500 resize-none"
        />
      </div>

      <div className="flex gap-2">
        <Button
          type="submit"
          disabled={submitting}
          className="bg-violet-500 hover:bg-violet-600"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Submit Review
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="border-zinc-700">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

// Main ProductReviews component
const ProductReviews = ({ productId, productName }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    average_rating: 0,
    review_count: 0,
    rating_distribution: {},
  });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/store/products/${productId}/reviews?page=${page}&limit=10&sort_by=${sortBy}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch reviews');
      
      const data = await response.json();
      
      if (page === 1) {
        setReviews(data.reviews);
      } else {
        setReviews((prev) => [...prev, ...data.reviews]);
      }
      
      setStats({
        average_rating: data.average_rating,
        review_count: data.review_count,
        rating_distribution: data.rating_distribution,
      });
      
      setHasMore(data.reviews.length === 10);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [productId, page, sortBy]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSubmitReview = async (reviewData) => {
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/store/products/${productId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          ...reviewData,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit review');
      }

      toast.success('Review submitted successfully!');
      setShowForm(false);
      setPage(1);
      fetchReviews();
    } catch (error) {
      toast.error(error.message || 'Failed to submit review');
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    if (!user) return;

    try {
      const response = await fetch(
        `${API_URL}/api/store/products/${productId}/reviews/${reviewId}/helpful?user_id=${user.id}`,
        { method: 'POST' }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to mark as helpful');
      }

      toast.success('Marked as helpful');
      fetchReviews();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-violet-400" />
            Customer Reviews
          </h3>
          <p className="text-sm text-zinc-500">{stats.review_count} reviews for {productName}</p>
        </div>
        
        {user && !showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-violet-500 hover:bg-violet-600"
          >
            Write a Review
          </Button>
        )}
      </div>

      {/* Rating summary */}
      <div className="grid md:grid-cols-2 gap-6 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-4xl font-bold text-white">{stats.average_rating.toFixed(1)}</div>
            <StarRating rating={Math.round(stats.average_rating)} />
            <div className="text-xs text-zinc-500 mt-1">{stats.review_count} reviews</div>
          </div>
        </div>
        
        <div className="space-y-1">
          {[5, 4, 3, 2, 1].map((rating) => (
            <RatingBar
              key={rating}
              rating={rating}
              count={parseInt(stats.rating_distribution[rating] || 0)}
              total={stats.review_count}
            />
          ))}
        </div>
      </div>

      {/* Review form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <ReviewForm
              productId={productId}
              onSubmit={handleSubmitReview}
              onCancel={() => setShowForm(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sort controls */}
      {reviews.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-500">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">No reviews yet. Be the first to review!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onMarkHelpful={handleMarkHelpful}
              userId={user?.id}
            />
          ))}
          
          {hasMore && (
            <Button
              variant="outline"
              onClick={() => setPage((p) => p + 1)}
              className="w-full border-zinc-700"
            >
              <ChevronDown className="w-4 h-4 mr-2" />
              Load More Reviews
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
